import { useQueries, useQueryClient } from '@tanstack/react-query';
import BehavioralScore from 'components/BehavioralScore/BehavioralScore';
import Graphic from 'components/Graphic/Graphic';
import Icon from 'components/Icon/Icon';
import MarkdownHTML from 'components/MarkdownHTML/MarkdownHTML';
import {
  GAD7QuestionKeys,
  PHQ9QuestionKeys,
  VISIT_TYPE_CONSTANTS,
  getGAD7Score,
  getPHQ9Score,
  getRelatedAnswerToDisplay,
  mappedQuesToAnsT,
} from 'context/visitContext';
import { questionnairesQuery, questionsQuery } from 'pages/PatientRecords/PatientRecords';
import { flatten, isEmpty, pluck, prop, uniqBy } from 'ramda';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { Question, QuestionAnswers, Visit } from 'types/types';
import readAWSS3File from 'utils/readAWSS3File';
import {
  calculateBMI,
  convertHeightToInches,
  formatArrayIntoSentence,
  isValidURL,
} from 'utils/utils';

function SymptomView({
  visitId,
  visit,
  questionnaires,
  onDoneLoading,
}: {
  visitId: string;
  visit: Visit;
  questionnaires: QuestionAnswers[];
  onDoneLoading: () => void;
}) {
  const [syncedSymptomPhotos, setSyncedSymptomPhotos] = React.useState<string[]>([]);
  const [openedRxRefill, setOpenedRxRefill] = React.useState<number>();
  const [isLoadingPhoto, setIsLoadingPhoto] = React.useState<string>();
  const queryClient = useQueryClient();

  const symptomsQueries = useQueries({
    queries: [
      {
        ...questionnairesQuery({ id: visitId }),
        enabled: !!(isEmpty(questionnaires) && visitId),
      },
      {
        ...questionsQuery(visit.type),
      },
    ],
  });

  const isLoading = isEmpty(questionnaires)
    ? symptomsQueries[0].isLoading
    : symptomsQueries[1].isLoading; // HACK, when questionnaires has value we base loading status on getting Questions

  const { questionnaire, questions } = React.useMemo(() => {
    let fQuestionnaire = (symptomsQueries[0]?.data || questionnaires || [])?.find(
      fndQuestionnaire => fndQuestionnaire.id === visitId,
    ) as QuestionAnswers;

    let fQuestions = (symptomsQueries[1]?.data || []) as Question[];

    // For Birth Control Visit Calculate the BMI and add to Answers
    if (visit.type === VISIT_TYPE_CONSTANTS.BIRTH_CONTROL && fQuestions.length > 0) {
      const height =
        [...(fQuestionnaire?.answers || [])].find(
          fndAnswer => fndAnswer.questionKey === 'HEIGHT' && Boolean(fndAnswer?.answers?.[0]),
        )?.answers?.[0] || '';

      const weight =
        [...(fQuestionnaire?.answers || [])].find(
          fndAnswer => fndAnswer.questionKey === 'WEIGHT' && Boolean(fndAnswer?.answers?.[0]),
        )?.answers?.[0] || '';

      const BMI = calculateBMI(convertHeightToInches(height), parseFloat(weight));

      if (BMI) {
        const BMIQuestion = {
          id: 'BMI',
          text: 'Approximated Body Mass Index (BMI)',
          type: 'OPEN-TEXT',
          error: '*Please provide an answer',
          priority: 1,
        } as Question;

        const BMIAnswer = {
          answers: [BMI],
          questionKey: BMIQuestion.id,
        };

        const indexOfWeightQuestion = fQuestions.findIndex(
          fndQuestion => fndQuestion.id === 'WEIGHT',
        );

        const modifiedFQuestions = [...fQuestions];

        modifiedFQuestions.splice(indexOfWeightQuestion + 1, 0, BMIQuestion);

        fQuestions = uniqBy(prop('id'), [...modifiedFQuestions]);

        fQuestionnaire = {
          ...fQuestionnaire,
          answers: [...fQuestionnaire.answers, BMIAnswer],
        };
      }
    }

    // For Rx-Refills Question, If the CURRENT_MEDICATIONS answer is OTHER. Add OTHER question.
    if (visit.type === VISIT_TYPE_CONSTANTS.RX_REFILLS && fQuestions.length > 0) {
      const otherCurrentMedQuestion = {
        id: 'OTHER_CURRENT_MEDICATIONS',
        text: 'Enter the current medications you’re taking.',
        type: 'OPEN-TEXT',
        error: '*Please provide an answer',
        priority: 1,
      } as Question;

      const indexOfCurrentMedication = fQuestions.findIndex(
        fndQuestion => fndQuestion.id === 'CURRENT_MEDICATIONS',
      );

      const modifiedFQuestions = [...fQuestions];

      modifiedFQuestions.splice(indexOfCurrentMedication + 1, 0, otherCurrentMedQuestion);

      fQuestions = uniqBy(prop('id'), [...modifiedFQuestions]);

      // THis will be removed later after we stop supporting old questions
      fQuestions = fQuestions.map(fndQuestion => {
        if (fndQuestion.id === 'CURRENT_MEDICATIONS') {
          return { ...fndQuestion, options: [] };
        }
        return fndQuestion;
      });
    }

    const isVisitForDependent =
      (fQuestionnaire?.answers || []).find(
        findAnswer => findAnswer.questionKey === 'VISIT_FOR_DEPENDENT',
      )?.answers?.[0] === 'YES';
    const nameOfDependent =
      (fQuestionnaire?.answers || []).find(
        findAnswer => findAnswer.questionKey === 'NAME_OF_DEPENDENT',
      )?.answers?.[0] || '';

    const validAnswers = new Set((fQuestionnaire?.answers || []).map(item => item.questionKey));

    fQuestions = [...fQuestions.filter(fndQuestion => validAnswers.has(fndQuestion.id))]
      .filter(findQuestion => findQuestion.id)
      .map(findQuestion => {
        return {
          ...findQuestion,
          ...(findQuestion.options && {
            options: findQuestion.options.map(findOption => ({
              ...findOption,
              text: findOption.text
                .replace('[insert provider name], ', '')
                .replace(/\*\*{insert mailing address}\*\*/g, ''),
            })),
          }),
          text: (isVisitForDependent && !isEmpty(nameOfDependent)
            ? findQuestion.alternateText || findQuestion.text
            : findQuestion.text
          )?.replace(/\*\*{insert person name}\*\*/g, nameOfDependent),
        };
      });

    return { questionnaire: fQuestionnaire, questions: fQuestions };
  }, [questionnaires, symptomsQueries, visit.type, visitId]);

  const mappedQuestionAnswers = questions
    .map(question => {
      return {
        ...mappedQuesToAnsT[visit.type.toLocaleLowerCase()](
          question,
          questionnaire?.answers || [],
          getRelatedAnswerToDisplay(questions, questionnaire?.answers || []),
        ),
        quesOptions: question?.options,
      };
    })
    .filter(fndAnswer => Boolean(fndAnswer.quesText) && fndAnswer.quesAnswer !== '-'); // Remove answers that are '-'

  // TODO: Check the mappedQuestionAnswers it doing some filtering out. That's why INTRO visit not showing answer
  // We are not to show it though

  const symptomResponses = mappedQuestionAnswers.filter(
    fndQuesAns =>
      visit.type !== VISIT_TYPE_CONSTANTS.RX_REFILLS ||
      !fndQuesAns.quesAnswer?.match(/\[([^\]]+)\]/),
  );

  const rxRefillsMedications = mappedQuestionAnswers.reduce(
    (accumulator: Array<Record<string, string>>, currentValue) => {
      // Skip if no bracket pattern found
      if (!currentValue.quesAnswer?.match(/\[([^\]]+)\]/)) {
        return accumulator;
      }

      // Try to parse and validate it's a JSON array format
      let answerOption: Array<Record<string, string>>;
      try {
        const parsed = JSON.parse(currentValue.quesAnswer);
        if (!Array.isArray(parsed)) {
          return accumulator;
        }
        answerOption = parsed;
      } catch {
        return accumulator;
      }

      return answerOption.map((findAnswer, index) => {
        const answerText = Object.values(findAnswer)[0];
        return {
          ...(answerText !== '-' && Boolean(answerText)
            ? { [currentValue.quesText]: Object.values(findAnswer)[0] }
            : {}),
          ...accumulator?.[index],
        };
      });
    },
    [],
  );

  // Rewrite this
  const uploadedPhotoLinks = symptomResponses.find(
    fndSymptom => fndSymptom.quesText === 'Photo',
  )?.quesAnswer;

  React.useEffect(() => {
    if (uploadedPhotoLinks) {
      let photoLinks = uploadedPhotoLinks;

      const match = photoLinks.match(/\[([^\]]+)\]/); // String in Brackets

      if (match && match[1]) {
        photoLinks = match[1].trim();
      }

      (async function syncPhotos() {
        const fndPhotoLinks = await Promise.all(
          (photoLinks.split(',') || [])
            .filter(fndLink => Boolean(fndLink) && isValidURL(fndLink))
            .map(async fndLink => readAWSS3File('document', fndLink)),
        );
        setSyncedSymptomPhotos(fndPhotoLinks);
      })();
    }
  }, [uploadedPhotoLinks]);

  React.useEffect(() => {
    if (isLoading) {
      return;
    }

    onDoneLoading();
  }, [isLoading, onDoneLoading]);

  const handleViewUploadedPhoto = async (photoFile: string) => {
    setIsLoadingPhoto(photoFile);

    const link = await queryClient.fetchQuery({
      queryKey: [photoFile],
      queryFn: () => readAWSS3File('document', photoFile),
    });

    setIsLoadingPhoto(undefined);

    window.open(link, '_blank');
  };

  const getBehavioralScore = () => {
    // calculate the GAD7 and PHQ9 Score
    const answers = [...(questionnaire?.answers || [])];

    const typeOfBHHealth = answers.find(
      fndAnswer => fndAnswer.questionKey === 'TYPE_BEHAVIORAL_HEALTH',
    )?.answers[0];

    let GAD7AnsScore = null;

    let PHQ9AnsScore = null;

    if (typeOfBHHealth === 'FEELING_ANXIOUS') {
      GAD7AnsScore = getGAD7Score(flatten(pluck('answers', answers)));
    }

    if (typeOfBHHealth === 'FEELING_DEPRESSED') {
      PHQ9AnsScore = getPHQ9Score(flatten(pluck('answers', answers)));
    }

    const GAD7Answers = flatten(
      pluck(
        'answers',
        answers.filter(fndAnswer => GAD7QuestionKeys.includes(fndAnswer.questionKey)),
      ),
    );

    const PHQ9Answers = flatten(
      pluck(
        'answers',
        answers.filter(fndAnswer => PHQ9QuestionKeys.includes(fndAnswer.questionKey)),
      ),
    );

    if (GAD7Answers.length > 0) {
      GAD7AnsScore = getGAD7Score(GAD7Answers);
    }

    if (PHQ9Answers.length > 0) {
      PHQ9AnsScore = getPHQ9Score(PHQ9Answers);
    }

    return {
      GAD7AnsScore: GAD7AnsScore || {
        value: 0,
        status: 'None to Minimal Anxiety',
      },
      PHQ9AnsScore: PHQ9AnsScore || {
        value: 0,
        status: 'None to Minimal Depression',
      },
    };
  };

  const integerToRoman = (integer: number) => {
    let num = integer;
    const romanNumerals = [
      { value: 1000, symbol: 'M' },
      { value: 900, symbol: 'CM' },
      { value: 500, symbol: 'D' },
      { value: 400, symbol: 'CD' },
      { value: 100, symbol: 'C' },
      { value: 90, symbol: 'XC' },
      { value: 50, symbol: 'L' },
      { value: 40, symbol: 'XL' },
      { value: 10, symbol: 'X' },
      { value: 9, symbol: 'IX' },
      { value: 5, symbol: 'V' },
      { value: 4, symbol: 'IV' },
      { value: 1, symbol: 'I' },
    ];

    let result = '';

    romanNumerals.forEach(numeral => {
      while (num >= numeral.value) {
        result += numeral.symbol;
        num -= numeral.value;
      }
    });

    return result;
  };

  const renderLoading = () => (
    <div className="flex flex-col">
      <div className="w-full grid tablet:grid-cols-2 gap-6 mb-7">
        {new Array(5).fill(null).map((_, index) => (
          <div key={index}>
            <h3 className="text-sm font-medium">
              <Skeleton width="70%" height={25} duration={1.4} />
            </h3>
            <div className="relative flex flex-row w-full space-x-3 mt-3">
              <Skeleton width="100%" height={20} duration={1.4} className="block !w-[7rem]" />
              <Skeleton width="100%" height={20} duration={1.4} className="block !w-[14rem]" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col">
      {isLoading || (!isEmpty(questionnaires) && isEmpty(symptomResponses)) ? ( // TODO
        <>{renderLoading()}</>
      ) : symptomResponses.length === 0 ? (
        <div className="flex flex-col justify-center items-center mt-8 mb-20">
          <Graphic id="graphicNoPendingVisit" className="h-[130px]" />
          <div className="text-center mt-3">
            <p className="font-medium">You’re all caught up.</p>
            <p className="text-[#7A7A7A] text-xs">Patient’s symptom responses will show here</p>
          </div>
        </div>
      ) : (
        <>
          <div className="fs-exclude w-full grid tablet:grid-cols-2 tablet:gap-9 gap-6 mb-7">
            {symptomResponses.map((fndSymptom, index) =>
              fndSymptom.quesText !== 'Photo' ? (
                <div key={fndSymptom.quesText} className="flex flex-row">
                  <span
                    className="hidden rounded-full w-7 h-7 bg-gray-100 border border-[#CDCDCD] tablet:inline-flex
                    items-center justify-center font-semibold text-[13.5px] mr-2 self-baseline shrink-0 mt-1"
                  >
                    {index + 1}
                  </span>
                  <div>
                    <h3 className="text-sm font-medium">{fndSymptom.quesText}</h3>
                    <p className="text-[15px] font-semibold mt-0 tablet:mt-2">
                      Answer:{' '}
                      <span
                        className="text-[15px] font-medium text-[#676767] underline underline-offset-2 text-balance break-words break-all whitespace-pre-line"
                        style={{ wordBreak: 'break-word' }}
                      >
                        <MarkdownHTML
                          content={fndSymptom.quesAnswer}
                          className="[&_p]:contents contents"
                        />
                        {/* {fndSymptom.quesAnswer} */}
                      </span>
                    </p>
                    {fndSymptom?.quesOptions && fndSymptom?.quesOptions?.length > 0 && (
                      <p className="text-xs font-medium mt-0 tablet:mt-2 text-[#9DA4A8]">
                        Options:{' '}
                        <span className="text-xs font-light text-[#9DA4A8]">
                          {formatArrayIntoSentence(pluck('text', fndSymptom.quesOptions))}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              ) : null,
            )}
          </div>

          {rxRefillsMedications.length > 0 ? (
            <>
              <hr className="hidden laptop:block border-b-[1px] border-t-0 border-[#e9ecef] mt-8 mb-3" />
              <div className="mb-7 w-full">
                <h3 className="text-[15px] font-medium mb-3 text-river-blue">
                  List of Medications
                </h3>
                {rxRefillsMedications.map((findRxRefills, index) => (
                  <div
                    key={index}
                    className={`border-[#CFD8DD] py-5 border-b-[1px] ${index === 0 ? 'pt-0' : ''}`}
                  >
                    <div className="flex flex-row items-center justify-between">
                      <h3 className="uppercase text-river-deep-orange">
                        MEDICATION {integerToRoman(index + 1)}
                      </h3>
                      <button
                        onClick={() =>
                          openedRxRefill === index
                            ? setOpenedRxRefill(undefined)
                            : setOpenedRxRefill(index)
                        }
                        type="button"
                        className="text-xs font-medium hover:opacity-75 text-river-blue"
                      >
                        View more
                      </button>
                    </div>
                    <div
                      className={`${
                        openedRxRefill === index ? '' : 'hidden'
                      } transform transition-all space-y-3 mt-2`}
                    >
                      {Object.entries(findRxRefills)
                        .reverse()
                        .map((refillAnswer, fIndex) => (
                          <div key={fIndex}>
                            <h3 className="text-sm font-medium">
                              {refillAnswer[0] !== 'Photo' ? refillAnswer[0] : 'Uploaded Photo(s)'}
                            </h3>

                            <p className="text-[15px] mt-0 tablet:mt-2">
                              {refillAnswer[0] !== 'Photo' ? (
                                <>
                                  Answer:{' '}
                                  <span className="text-[15px] font-medium text-[#676767] underline underline-offset-2">
                                    {refillAnswer[1]}
                                  </span>
                                </>
                              ) : refillAnswer[1]?.split(',')?.length > 0 ? (
                                refillAnswer[1].split(',').map((photo, pIndex, pArray) => (
                                  <button
                                    key={pIndex}
                                    onClick={() => handleViewUploadedPhoto(photo)}
                                    type="button"
                                    disabled={isLoadingPhoto === photo}
                                    onMouseEnter={() =>
                                      queryClient.prefetchQuery({
                                        queryKey: [photo],
                                        queryFn: () => readAWSS3File('document', photo),
                                      })
                                    }
                                    className="text-river-blue mt-1 mr-4 items-center border border-[#C4C9CC] rounded-full px-3 pt-[1.5px] pb-[2px]"
                                  >
                                    {isLoadingPhoto !== photo ? (
                                      <span className="text-xs hover:underline underline-offset-2">
                                        {pArray.length === 1
                                          ? `Click to view Photo`
                                          : `View Photo ${pIndex + 1}`}
                                        <span
                                          className="w-5 h-5 bg-river-blue rounded-full inline-flex items-center 
                                          justify-center ml-2 flex-shrink-0 cursor-default"
                                        >
                                          <Icon id="checkmark" className="text-white w-2 h-2" />
                                        </span>
                                      </span>
                                    ) : (
                                      <>
                                        please wait...{' '}
                                        <Icon id="loader" className="ml-3 w-6 h-6 inline-flex" />
                                      </>
                                    )}
                                  </button>
                                ))
                              ) : null}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {visit.type === VISIT_TYPE_CONSTANTS.BEHAVIORAL_VISITS ? (
            <div className="fs-exclude w-full mb-7">
              <hr className="hidden laptop:block border-b-[1px] border-t-0 border-[#e9ecef] mb-5" />
              <div className="w-full grid tablet:grid-cols-2 tablet:gap-9 gap-6 mb-7">
                <div>
                  <h3 className="text-sm font-medium">Generalized Anxiety Disorder - 7</h3>
                  <p className="text-[15px] font-medium mt-0 tablet:mt-2">
                    <BehavioralScore
                      type="GAD7"
                      score={getBehavioralScore().GAD7AnsScore.value}
                      status={getBehavioralScore().GAD7AnsScore.status}
                    />
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium">Patient Health Questionnaire - 9</h3>
                  <p className="text-[15px] font-medium mt-0 tablet:mt-2">
                    <BehavioralScore
                      type="PHQ9"
                      score={getBehavioralScore().PHQ9AnsScore.value}
                      status={getBehavioralScore().PHQ9AnsScore.status}
                    />
                  </p>
                </div>
              </div>
            </div>
          ) : null}

          {syncedSymptomPhotos.length > 0 ? (
            <>
              <hr className="hidden laptop:block border-b-[1px] border-t-0 border-[#e9ecef] mt-8 mb-3" />
              <div className="w-full mb-7">
                <h3 className="text-[15px] font-medium mb-3 text-river-blue">
                  Photos of Symptoms, Conditions, Prescriptions, etc.
                </h3>
                <div className="flex flex-row gap-8 flex-wrap">
                  {syncedSymptomPhotos.map((fndPhoto, index) =>
                    fndPhoto ? (
                      <div
                        key={index}
                        title="Click to view Photo"
                        className="rounded-xl cursor-pointer flex justify-center p-5 bg-[#EEFBFF]
                        w-[280px] h-[240px] border-[1.5px] border-dashed border-river-blue"
                      >
                        <a href={fndPhoto} target="_blank" rel="noreferrer" className="contents">
                          <img className="" src={fndPhoto} alt="view symptom" />
                        </a>
                      </div>
                    ) : null,
                  )}
                </div>
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

export default SymptomView;
